import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import UnifiedListingDialog from "@/components/UnifiedListingDialog";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import MandateForm from "@/components/mandates/MandateForm";
import MandateCard from "@/components/mandates/MandateCard";
import { logWarn } from "@/lib/logger";

const initialForm = (user, defaultType) => ({
  mandate_type: defaultType,
  signer_name: user?.name || "",
  signer_email: user?.email || "",
  signer_fiscal_code: "",
  signer_address: "",
  property_ref: "",
  property_type: "",
  search_budget: "",
  search_area: "",
  exclusivity_months: 6,
  commission_terms: "",
});

/** Mandate creation form + list. `defaultType` = "sale" (owner) | "search" (buyer). */
export function MandatesPanel({ defaultType = "search" }) {
  const { lang, user } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => initialForm(user, defaultType));
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpEmail, setOtpEmail] = useState("");

  // Open MandatesPanel form when UnifiedListingDialog dispatches "open-mandate-form"
  useEffect(() => {
    const handler = (e) => {
      const kind = e?.detail?.kind === "search" ? "search" : "sale";
      setForm(initialForm(user, kind));
      setOpen(true);
    };
    window.addEventListener("open-mandate-form", handler);
    return () => window.removeEventListener("open-mandate-form", handler);
  }, [user]);

  const load = async () => {
    try {
      const { data } = await api.get("/mandates");
      setItems(data);
    } catch (e) { logWarn("Mandates load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const requestOtp = async () => {
    try {
      const { data } = await api.post("/otp/request", { purpose: "mandate", signer_email: form.signer_email });
      setOtpEmail(data.email || "");
      setOtpRequested(true);
      toast.success(`Email → ${data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP error");
    }
  };

  const preview = async () => {
    try {
      const payload = {
        mandate_type: form.mandate_type,
        signer_name: form.signer_name,
        signer_email: form.signer_email,
        signer_fiscal_code: form.signer_fiscal_code,
        signer_address: form.signer_address,
        property_ref: form.property_ref,
        property_type: form.property_type || null,
        search_budget: form.search_budget ? parseFloat(form.search_budget) : null,
        search_area: form.search_area,
        exclusivity_months: parseInt(form.exclusivity_months, 10) || 6,
        commission_terms: form.commission_terms,
      };
      const resp = await api.post("/mandates/preview", payload, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Preview error");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        search_budget: form.search_budget ? parseFloat(form.search_budget) : null,
        exclusivity_months: parseInt(form.exclusivity_months, 10) || 6,
        accepted: true,
        signature_image_base64: signature,
        otp_code: otpCode || null,
      };
      await api.post("/mandates", payload);
      toast.success(lang === "it" ? "Mandato firmato" : "Mandate signed");
      setOpen(false);
      setAccepted(false);
      setSignature(null);
      setOtpRequested(false);
      setOtpCode("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const download = async (m) => {
    const resp = await api.get(`/mandates/${m.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mandato-${m.mandate_type}-${m.id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const heading = defaultType === "sale" ? t("mandates_sale_heading", lang) : t("searches_tab", lang);
  const cta = defaultType === "sale" ? t("mandate_cta_sale", lang) : t("mandate_cta_search", lang);
  const dialogTitle = form.mandate_type === "sale" ? t("mandate_sale_title", lang) : t("mandate_search_title", lang);

  return (
    <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-6" data-testid="mandates-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A880] mb-1">Aaron &amp; Partners</p>
          <h3 className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{heading}</h3>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <UnifiedListingDialog onCreated={load} />
          <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {dialogTitle}
              </DialogTitle>
            </DialogHeader>
            <MandateForm
              form={form}
              setForm={setForm}
              accepted={accepted}
              setAccepted={setAccepted}
              setSignature={setSignature}
              otpRequested={otpRequested}
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              otpEmail={otpEmail}
              onRequestOtp={requestOtp}
              onPreview={preview}
            />
            <DialogFooter>
              <Button
                disabled={!accepted || busy || !form.signer_name || !form.signer_email}
                onClick={submit}
                data-testid="mandate-submit-btn"
                className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full disabled:opacity-40"
              >
                {busy ? "…" : t("sign_and_generate", lang)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!items.length && <p className="text-sm text-[#9A8B72] py-10 text-center">{t("no_data", lang)}</p>}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((m) => <MandateCard key={m.id} mandate={m} onDownload={download} onChanged={load} isAdmin={user?.role === "admin"} />)}
      </div>
    </Card>
  );
}
